/**
 * A deliberately small Markdown subset for the app's user manual.
 *
 * The widget ships to third-party pages, so a full parser is weight the manual
 * does not justify: headings, emphasis, code, links and lists cover what app
 * owners actually write. Everything else renders as the literal text it is.
 *
 * Safety: the source is escaped FIRST, so the only tags in the output are the
 * ones produced here, and link targets are restricted to http/https/mailto —
 * the manual is org-authored, but it renders on a customer's own site.
 */

const SAFE_LINK = /^(https?:|mailto:)/i

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Inline spans, applied to already-escaped text. */
function renderInline(escaped: string): string {
  return escaped
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (match, label: string, href: string) =>
      SAFE_LINK.test(href)
        ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`
        : match
    )
}

/**
 * Render the supported subset to an HTML string.
 *
 * Returns "" for empty input so callers can treat it as "nothing to show".
 */
export function renderMarkdown(source: string): string {
  if (!source.trim()) return ''

  const lines = escapeHtml(source).split('\n')
  const out: string[] = []
  let listTag: 'ul' | 'ol' | null = null
  let paragraph: string[] = []

  const flushParagraph = () => {
    if (paragraph.length > 0) {
      out.push(`<p>${renderInline(paragraph.join(' '))}</p>`)
      paragraph = []
    }
  }
  const closeList = () => {
    if (listTag) {
      out.push(`</${listTag}>`)
      listTag = null
    }
  }
  const openList = (tag: 'ul' | 'ol') => {
    if (listTag !== tag) {
      closeList()
      out.push(`<${tag}>`)
      listTag = tag
    }
  }

  for (const line of lines) {
    const trimmed = line.trim()

    if (!trimmed) {
      flushParagraph()
      closeList()
      continue
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(trimmed)
    if (heading) {
      flushParagraph()
      closeList()
      // Cap at h4: the manual sits inside the host page's document outline.
      const level = Math.min(heading[1].length + 2, 6)
      out.push(`<h${level}>${renderInline(heading[2])}</h${level}>`)
      continue
    }

    const bullet = /^[-*+]\s+(.*)$/.exec(trimmed)
    if (bullet) {
      flushParagraph()
      openList('ul')
      out.push(`<li>${renderInline(bullet[1])}</li>`)
      continue
    }

    const numbered = /^\d+[.)]\s+(.*)$/.exec(trimmed)
    if (numbered) {
      flushParagraph()
      openList('ol')
      out.push(`<li>${renderInline(numbered[1])}</li>`)
      continue
    }

    closeList()
    paragraph.push(trimmed)
  }

  flushParagraph()
  closeList()
  return out.join('')
}
